import {createSelector} from 'reselect';
import {convertDashesToSlashes, subtractDaysFromDate, isFuture} from '../utils/dateHelper';

import {labelMaker} from '../../server/utils/labelMaker';
import {getTranslations, getTripData} from './store';
import {
    getHostType,
    getIpmGuaranteedPricingActive,
    getLegacyLodgingPolicy,
    getRateDetails
} from './listing';
import {getLocaleJS} from './system';
import {getValidDates} from './tripDates';

// peel these objects off rateDetails:
// lising.rateDetails.cancellationPolicy
// lising.rateDetails.unitRentalPolicy.cancellationRefundPolicyPeriods
const getPolicyObj = createSelector(
    [getLegacyLodgingPolicy, getRateDetails],
    (legacyLodgingPolicy = {}, rateDetails = {}) => {
        const cancellationPolicyObj = rateDetails.cancellationPolicy;
        const unitRentalPolicy = rateDetails.unitRentalPolicy || {};
        const cancellationPolicyObjUrp = unitRentalPolicy.cancellationRefundPolicyPeriods;
        const lodgingPolicyCancellationPolicy = legacyLodgingPolicy.lodgingPolicyCancellationPolicy;

        return {
            cancellationPolicyObj,
            cancellationPolicyObjUrp,
            lodgingPolicyCancellationPolicy
        };
    }
);

// traverse the possible cancellationPolicy objects and populate an array with usable data
// use <cancellationPolicy active="true" minDaysCancellable="-2" refundPercent="0.0"/> to pick path
// minDaysCancellable="-2" => use unitRentalPolicy
// minDaysCancellable="-1" => policy not available
// minDaysCancellable="0+" => use policyArray from legacyLodgingPolicy
// use cancellationPolicy as backup
const getPolicyArray = createSelector(
    [getPolicyObj],
    (policyObj) => {
        const cancellationPolicyObj = policyObj.cancellationPolicyObj;
        const cancellationPolicyObjUrp = policyObj.cancellationPolicyObjUrp;
        const lodgingPolicyCancellationPolicy = policyObj.lodgingPolicyCancellationPolicy;

        let policyArray = [];

        if (cancellationPolicyObj && cancellationPolicyObj.minDaysCancellable === -2) {
            policyArray = cancellationPolicyObjUrp || [];
        } else if (lodgingPolicyCancellationPolicy) {
            policyArray = lodgingPolicyCancellationPolicy.policyPeriods;
        } else if (cancellationPolicyObj) {
            policyArray = [
                {
                    limitedTo: cancellationPolicyObj.minDaysCancellable,
                    refundPercent: cancellationPolicyObj.refundPercent
                }
            ];
        }

        return policyArray;
    }
);

// return whether the listing
// a) has a valid cancellation policy
// b) offers a greater than 0% refund
const getPolicyStatus = createSelector(
    [getPolicyArray],
    (policyArray) => {
        let hasPolicy = false;
        let allowsRefunds = false;

        for (let policy of policyArray) {
            let numDaysNotice = policy.limitedTo;
            let refundPercent = policy.refundPercent;

            if (refundPercent > 0) {
                hasPolicy = true;
                allowsRefunds = true;
            } else if (refundPercent === 0 && numDaysNotice === 0) {
                hasPolicy = true;
                allowsRefunds = false;
            }
        }

        return {
            hasPolicy,
            allowsRefunds
        };
    }
);

// Returns true if any of the refund periods occur in the future
const getDatesQualify = createSelector(
    [getTripData, getPolicyStatus, getPolicyArray],
    (tripData, policyStatus, policyArray) => {
        const {arriveDateText} = tripData;
        let datesQualify = false;

        if (!policyStatus.hasPolicy || !policyStatus.allowsRefunds) {
            return null;
        }

        for (let policy of policyArray) {
            const numDaysNotice = policy.limitedTo;
            const relativeDate = subtractDaysFromDate(arriveDateText, numDaysNotice);

            datesQualify = isFuture(relativeDate) || datesQualify;
        }

        return datesQualify;
    }
);

// "x refund if cancelled x days out"
const getCancellationPolicyUndated = createSelector(
    [getPolicyArray, getPolicyStatus, getTranslations],
    (policyArray, policyStatus, trans) => {
        let policyUndated = '';

        if (!policyStatus.hasPolicy || !policyStatus.allowsRefunds) {
            return policyUndated;
        }

        for (let policy of policyArray) {
            let numDaysNotice = policy.limitedTo;
            let refundPercent = policy.refundPercent;
            let policySubstring;

            policySubstring = labelMaker(
                trans.cancellationPolicyUndated,
                refundPercent.toString(),
                numDaysNotice.toString()
            );
            policyUndated = `${policySubstring} ${policyUndated}`;
        }

        return policyUndated;
    }
);

// "x refund until xx/xx/xx"
/* eslint-disable complexity */
const getCancellationPolicyDated = createSelector(
    [
        getTripData,
        getPolicyArray,
        getPolicyStatus,
        getCancellationPolicyUndated,
        getDatesQualify,
        getValidDates,
        getLocaleJS,
        getTranslations
    ],
    (
        tripData,
        policyArray,
        policyStatus,
        cancellationPolicyUndated,
        datesQualify,
        validDates,
        localeJS,
        trans
    ) => {
        const {arriveDateText} = tripData;
        const someDateIsFuture = datesQualify;
        let policyDated = '';

        if (!policyStatus.hasPolicy || !policyStatus.allowsRefunds) {
            return policyDated;
        }

        if (!validDates) {
            return cancellationPolicyUndated;
        }

        for (let policy of policyArray) {
            const numDaysNotice = policy.limitedTo;
            const refundPercent = policy.refundPercent;
            let policySubstring;
            let relativeDate = subtractDaysFromDate(arriveDateText, numDaysNotice);
            let relativeDateFormatted = new Date(
                convertDashesToSlashes(relativeDate)
            ).toLocaleDateString(localeJS, {day: 'numeric', month: 'numeric', year: 'numeric'});
            let thisDateIsFuture = isFuture(relativeDate);

            // if any dates are in the future, generate string with those cancellation periods
            // {percent}% refund for cancellations requested by {date} at 11:59 AM CST.
            if (thisDateIsFuture || !someDateIsFuture) {
                policySubstring = labelMaker(trans.cancellationPolicyDated, {
                    percent: refundPercent.toString(),
                    date: relativeDateFormatted
                });
                policyDated = `${policySubstring} ${policyDated}`;
            }
        }

        return policyDated;
    }
);
/* eslint-enable complexity */

// "The dates selected do not qualify for a refund if your trip is cancelled."
const getMessageDatesDontQualify = createSelector(
    [getPolicyStatus, getDatesQualify, getTranslations],
    (policyStatus, datesQualify, trans) => {
        let datesDontQualifyMessage = '';

        if (!policyStatus.hasPolicy || !policyStatus.allowsRefunds) {
            return datesDontQualifyMessage;
        }

        if (!datesQualify) {
            datesDontQualifyMessage = trans.cancellationPolicyDoesntQualify;
        }

        return datesDontQualifyMessage;
    }
);

// Canceled bookings will not receive a refund
const getCancellationPolicyNoRefunds = createSelector(
    [getPolicyArray, getPolicyStatus, getTranslations],
    (policyArray, policyStatus, trans) => {
        let refundsNotAllowedMessage = '';

        if (policyStatus.hasPolicy && !policyStatus.allowsRefunds) {
            refundsNotAllowedMessage = trans.cancellationPolicyNoRefunds;
        }

        return refundsNotAllowedMessage;
    }
);

// Please ask the manager or check their rental agreement when booking the property.
const getCancellationPolicyNotAvailable = createSelector(
    [getPolicyArray, getPolicyStatus, getTranslations],
    (policyArray, policyStatus, trans) => {
        let policyUnavailableMessage = '';

        if (!policyStatus.hasPolicy) {
            policyUnavailableMessage = trans.policyUnavailableMessageOwner;
        }

        return policyUnavailableMessage;
    }
);

// <strong>Free Cancellation</strong> until xx/xx/xxxx
/* eslint-disable complexity */
const getCancellationPolicyFreeCancellation = createSelector(
    [getTripData, getPolicyArray, getPolicyStatus, getTranslations, getLocaleJS, getValidDates],
    (tripData, policyArray, policyStatus, trans, localeJS, validDates) => {
        const {arriveDateText} = tripData;
        let freeCancellationTeaser = '';

        if (!policyStatus.hasPolicy || !policyStatus.allowsRefunds || !validDates) {
            return freeCancellationTeaser;
        }

        for (let policy of policyArray) {
            const refundPercent = policy.refundPercent;

            if (refundPercent === 100) {
                const relativeDate = subtractDaysFromDate(arriveDateText, policy.limitedTo);
                const relativeDateFormatted = new Date(
                    convertDashesToSlashes(relativeDate)
                ).toLocaleDateString(localeJS, {day: 'numeric', month: 'numeric', year: 'numeric'});
                const dateIsFuture = isFuture(relativeDate);

                if (dateIsFuture) {
                    freeCancellationTeaser = {
                        text: trans.freeCancellationTeaser,
                        date: relativeDateFormatted,
                        tooltipTitle: trans.cancellationTooltipTitle
                    };
                }
            }
        }

        return freeCancellationTeaser;
    }
);
/* eslint-enable complexity */

const getCutOffTime = createSelector(
    [getLocaleJS],
    (localeJS) => {
        return new Date(2000, 0, 1, 11, 59).toLocaleTimeString(localeJS, {
            hour: 'numeric',
            minute: 'numeric'
        }); // 11:59 AM
    }
);

const getCancellationPolicyFullText = createSelector(
    [getTripData, getPolicyArray, getTranslations, getLocaleJS, getCutOffTime],
    (tripData, policyArray, trans, localeJS, cutOffTime) => {
        const {arriveDateText} = tripData;
        let relativeDateFormatted = '';
        let title = '';

        let cancellation = policyArray
            .map((policy) => {
                const relativeDate = subtractDaysFromDate(arriveDateText, policy.limitedTo);
                relativeDateFormatted = new Date(
                    convertDashesToSlashes(relativeDate)
                ).toLocaleDateString(localeJS, {day: 'numeric', month: 'numeric', year: 'numeric'});

                if (policy.refundPercent === 100) {
                    title = labelMaker(trans.cancellationTooltipTitle, {
                        date: relativeDateFormatted
                    });
                    return labelMaker(trans.cancellationTooltipRefundDeadline, {
                        time: cutOffTime,
                        date: relativeDateFormatted
                    });
                }
                return labelMaker(trans.cancellationTooltipRefundDetails, {
                    percent: policy.refundPercent,
                    time: cutOffTime,
                    date: relativeDateFormatted
                });
            })
            .reverse()
            .join(' ');

        return {
            title,
            cancellation,
            serviceFee: trans.cancellationRefundServiceFee
        };
    }
);

const getCancellationPolicy = createSelector(
    [
        getCancellationPolicyUndated,
        getCancellationPolicyDated,
        getMessageDatesDontQualify,
        getCancellationPolicyNotAvailable,
        getCancellationPolicyNoRefunds,
        getCancellationPolicyFreeCancellation,
        getCancellationPolicyFullText,
        getTranslations
    ],
    (
        cancellationPolicyUndated,
        cancellationPolicyDated,
        messageDatesDontQualify,
        cancellationPolicyNotAvailable,
        cancellationPolicyNoRefunds,
        cancellationPolicyFreeCancellation,
        cancellationPolicyFullText,
        translations
    ) => {
        const labels = {
            cancellationPolicyHeader: translations.cancellationPolicyHeader,
            cancellationPolicyHeaderManager: translations.cancellationPolicyHeaderManager,
            cancellationPolicyHeaderOwner: translations.cancellationPolicyHeaderOwner
        };

        return {
            labels,
            cancellationPolicyUndated,
            cancellationPolicyDated,
            messageDatesDontQualify,
            cancellationPolicyNotAvailable,
            cancellationPolicyNoRefunds,
            cancellationPolicyFreeCancellation,
            cancellationPolicyFullText
        };
    }
);

const useCancellationPolicy = createSelector(
    [getCancellationPolicy, getHostType, getIpmGuaranteedPricingActive],
    (cancellationPolicy, hostType, ipmGuaranteedPricingActive) => {
        return hostType.ipm && !ipmGuaranteedPricingActive ? null : cancellationPolicy;
    }
);

export default useCancellationPolicy;

export {
    getPolicyObj,
    getPolicyArray,
    getPolicyStatus,
    getDatesQualify,
    getCancellationPolicyUndated,
    getCancellationPolicyDated,
    getMessageDatesDontQualify,
    getCancellationPolicyNoRefunds,
    getCancellationPolicyNotAvailable,
    getCancellationPolicyFreeCancellation,
    getCancellationPolicy,
    useCancellationPolicy,
    getCutOffTime,
    getCancellationPolicyFullText
};
